import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ProductCard } from "@/components/ProductCard";
import { ArrowRight, Scissors, Sparkles, Heart } from "lucide-react";

export default function Home() {
  const { settings, shopCategories: categories } = useSettings();
  const [products, setProducts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const hp = settings.homepage || {};

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 8))).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-kys-blush">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-24">
          <div className="animate-fade-up">
            <p className="label-accent text-kys-accent">{settings.tagline || "Handmade Performance Wear"}</p>
            <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight text-kys-ink sm:text-5xl lg:text-6xl">
              {hp.hero_title || "Handmade to Command the Stage"}
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-kys-charcoal">
              {hp.hero_subtitle || "Small-batch performance wear made by hand."}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/shop" data-testid="hero-shop-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-kys-ink px-7 py-3.5 text-sm font-semibold text-white transition hover:bg-kys-accent">
                Shop Ready to Ship <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/custom-order" data-testid="hero-custom-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-kys-ink px-7 py-3.5 text-sm font-semibold text-kys-ink transition hover:bg-kys-ink hover:text-white">
                Start a Custom Order
              </Link>
            </div>
          </div>
          <div className="animate-fade-in">
            <div className="aspect-[4/5] overflow-hidden rounded-lg border border-kys-blush-border bg-white shadow-2xl shadow-kys-blush-soft">
              {hp.hero_image && <img src={fileUrl(hp.hero_image)} alt="Kys Boutique" className="h-full w-full object-cover" />}
            </div>
          </div>
        </div>
      </section>

      {/* Shop by category */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="label-accent text-kys-accent">Browse</p>
            <h2 className="mt-2 font-serif text-3xl tracking-tight text-kys-ink sm:text-4xl">Shop by Category</h2>
          </div>
          <Link to="/shop" className="hidden text-sm font-medium text-kys-charcoal hover:text-kys-accent sm:inline-flex">View all →</Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-5">
          {categories.map((c, i) => (
            <Link key={c.id} to={`/shop?category=${c.id}`} data-testid={`home-cat-${c.slug}`}
              className="group flex aspect-square animate-fade-up flex-col items-center justify-center rounded-md border border-kys-blush-border bg-white text-center transition hover:border-kys-accent hover:bg-kys-blush"
              style={{ animationDelay: `${i * 60}ms` }}>
              <span className="font-serif text-xl text-kys-ink group-hover:text-kys-accent sm:text-2xl">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured pieces */}
      {products.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex items-end justify-between">
              <div>
                <p className="label-accent text-kys-accent">Just In</p>
                <h2 className="mt-2 font-serif text-3xl tracking-tight text-kys-ink sm:text-4xl">Featured Pieces</h2>
              </div>
              <Link to="/shop" className="text-sm font-medium text-kys-charcoal hover:text-kys-accent">Shop all →</Link>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-8">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Build your set banner */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-lg bg-kys-ink">
          <div className="grid grid-cols-1 items-center gap-8 p-8 sm:p-12 lg:grid-cols-2 lg:p-16">
            <div>
              <p className="label-accent text-kys-accent">Mix & Match</p>
              <h2 className="mt-3 font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-5xl">Build Your Own Set</h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-white/70">
                Combine Kys Boutique designs — a top here, a bottom there, an optional skirt — into a one-of-a-kind look. Choose your fabrics per piece and send it over as a custom quote request.
              </p>
              <Link to="/builder" data-testid="home-builder-cta"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-kys-accent px-7 py-3.5 text-sm font-semibold text-white transition hover:bg-kys-accent-dark">
                Start Building <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[Scissors, Sparkles, Heart].map((Icon, i) => (
                <div key={i} className="flex aspect-square items-center justify-center rounded-md border border-white/10 bg-white/5">
                  <Icon className="h-8 w-8 text-kys-accent" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Handmade / custom explanation */}
      <section className="bg-kys-blush py-16">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <p className="label-accent text-kys-accent">Handmade</p>
          <h2 className="mt-3 font-serif text-3xl tracking-tight text-kys-ink sm:text-4xl">Made by Hand, Made for You</h2>
          <p className="mt-5 text-base leading-relaxed text-kys-charcoal">{hp.custom_blurb || hp.handmade_blurb}</p>
          <div className="mt-8 grid grid-cols-1 gap-6 text-left sm:grid-cols-2">
            <div className="rounded-md border border-kys-blush-border bg-white p-6">
              <h3 className="font-serif text-2xl text-kys-ink">Ready to Ship</h3>
              <p className="mt-2 text-sm text-kys-charcoal">Already-made pieces, many one-of-a-kind. Purchase and it ships to you.</p>
              <Link to="/shop" className="mt-4 inline-flex text-sm font-semibold text-kys-accent hover:text-kys-accent-dark">Shop now →</Link>
            </div>
            <div className="rounded-md border border-kys-blush-border bg-white p-6">
              <h3 className="font-serif text-2xl text-kys-ink">Custom Orders</h3>
              <p className="mt-2 text-sm text-kys-charcoal">Build from Kys designs & available fabrics. Submitted for review — no payment until approved.</p>
              <Link to="/custom-order" className="mt-4 inline-flex text-sm font-semibold text-kys-accent hover:text-kys-accent-dark">Request a quote →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <p className="label-accent text-kys-accent">The Look</p>
            <h2 className="mt-2 font-serif text-3xl tracking-tight text-kys-ink sm:text-4xl">Featured Looks</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
            {gallery.map((g, i) => (
              <div key={g.id} className="hover-zoom aspect-[3/4] overflow-hidden rounded-md border border-kys-blush-border animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <img src={fileUrl(g.image)} alt={g.image?.alt || g.caption || "Featured look"} loading="lazy" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Instagram placeholder */}
      <section className="border-t border-kys-blush-border bg-white py-14">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-serif text-2xl text-kys-ink sm:text-3xl">Follow along</h2>
          <p className="mt-2 text-sm text-kys-muted">
            {hp.instagram_handle ? `@${hp.instagram_handle}` : "[Social handle to be added in admin]"}
          </p>
          {hp.instagram_url ? (
            <a href={hp.instagram_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex rounded-full border border-kys-ink px-6 py-2.5 text-sm font-semibold text-kys-ink hover:bg-kys-ink hover:text-white">Visit Instagram</a>
          ) : null}
        </div>
      </section>
    </div>
  );
}
